import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { Button, CloseButton } from '@spotahome/ui-library';
import Metrics from '@spotahome/soyuz-metrics';
import Tooltip from '@spotahome/ui-library/lib/components/Tooltip/Tooltip';

import { trans } from '@spotahome/soyuz/client';

import { useMatcherContext } from '@common/context/MatcherContext';
import InformationIcon from '@sru/components/InformationIcon';
import ShowApartmentNumber from '@common/components/ShowApartmentNumber';

import PriceSliderFilter from '../../../LEGACY/javascripts/search/components/Filters/PriceSliderFilter';
import DatePickerFilter from '../../../LEGACY/javascripts/search/components/Filters/DatePickerFilter';

import NUMBER_ROOMS_TOGGLE from '../../../LEGACY/javascripts/search/components/Filters/rooms-config';
import FilterGroup from '../../../LEGACY/javascripts/search/components/FilterGroup';
import CheckBox from '../../../LEGACY/javascripts/ui/form/form-checkbox';
import GroupToggle from '../../../LEGACY/javascripts/ui/form/form-group-toggle';

import './MatcherFilters.scss';

const CONTENT_LIST_TRANS_KEYS = [
  'search.filter.property.tooltip.whole_apartment',
  'search.filter.property.tooltip.studio',
  'search.filter.property.tooltip.rooms',
  'search.filter.property.tooltip.residence'
];

const renderInfoToolTip = content => {
  return (
    <Tooltip color="blue-cerulean" tooltip={content}>
      <InformationIcon className="info__icon" size={20} />
    </Tooltip>
  );
};

const MatcherFilters = ({ close, next }) => {
  const {
    requestHomecards,
    maxPriceInCity,
    currency,
    initialUnitPrices,
    minimumStaying,
    matcherFilters,
    setMatcherFilters
  } = useMatcherContext();

  useEffect(() => {
    Metrics.ga.sendEvent('Matcher', 'MatcherFilters', 'pageview');
  }, []);

  const isValueChecked = (arr = [], value) => arr.includes(value);

  const handleToggleCheckbox = (name, value, isChecked) => {
    let options = matcherFilters[name];
    if (isChecked) {
      options = [...options, value];
    } else {
      options = options.filter(option => option !== value);
    }

    setMatcherFilters({ ...matcherFilters, [name]: options });
  };

  const handleApartmentsFilterTypeCheckbox = value => isChecked => {
    handleToggleCheckbox('type[]', value, isChecked);
  };

  const handleToggleFilterTypeCheckbox = value => isChecked => {
    handleToggleCheckbox('type[]', value, isChecked);
  };

  const isPropertyTypeChecked = value => {
    return isValueChecked(matcherFilters['type[]'], value);
  };

  const handleTopologyClick = (name, value, isChecked) =>
    handleToggleCheckbox('topology[]', value, isChecked);

  const getSelectedTopologies = () => {
    return matcherFilters['topology[]'].join(',');
  };

  const handleClose = () => {
    Metrics.ga.sendEvent('Matcher', 'close filters', 'button click');
    close();
  };

  const handleNext = async () => {
    Metrics.ga.sendEvent('Matcher', 'go to swiper', 'button click');
    await requestHomecards();
    next();
  };

  return (
    <>
      <div className="matcher__filters-page">
        <CloseButton
          className="matcher__close-button"
          onClose={handleClose}
          size="small"
        />
        <h2>{trans('search.matcher.filters.title')}</h2>
        <DatePickerFilter
          from={matcherFilters['move-in']}
          flexibleMoveIn={matcherFilters.flexibleMoveIn}
          flexibleMoveOut={matcherFilters.flexibleMoveOut}
          minimumStaying={minimumStaying}
          to={matcherFilters['move-out']}
          updateFiltersWith={filter => {
            setMatcherFilters({ ...matcherFilters, ...filter });
          }}
        />
        <PriceSliderFilter
          budget={matcherFilters.budget}
          currency={currency}
          maxPriceInCity={maxPriceInCity}
          unitPrices={initialUnitPrices}
          updateFiltersWith={filter => {
            setMatcherFilters({ ...matcherFilters, ...filter });
          }}
        />

        <FilterGroup
          classModifier="property-type-checkboxes"
          label={trans('search.filter.property.type')}
          tooltip={() =>
            renderInfoToolTip(
              <ul className="search-filter-tooltip">
                {CONTENT_LIST_TRANS_KEYS.map(transKey => (
                  <li
                    key={transKey}
                    dangerouslySetInnerHTML={{ __html: trans(transKey) }}
                  />
                ))}
              </ul>
            )
          }
        >
          <CheckBox
            id="apartment-id"
            label={trans('search.filter.apartments')}
            onChange={handleApartmentsFilterTypeCheckbox('apartments')}
            checked={isPropertyTypeChecked('apartments')}
            tracking="ga-search-form-apartments"
            soyuzTracking={{
              eventCategory: 'Search',
              eventAction: 'matcher filters',
              eventLabel: 'apartments checked'
            }}
          />
          {isPropertyTypeChecked('apartments') && (
            <div>
              <ShowApartmentNumber number={matcherFilters['topology[]']} />
              <GroupToggle
                onChange={handleTopologyClick}
                selectedValues={getSelectedTopologies()}
                options={NUMBER_ROOMS_TOGGLE}
                key="specific-key-room"
              />
            </div>
          )}
          <CheckBox
            id="studios-id"
            label={trans('search.filter.studios')}
            onChange={handleToggleFilterTypeCheckbox('studios')}
            checked={isPropertyTypeChecked('studios')}
            soyuzTracking={{
              eventCategory: 'Search',
              eventAction: 'matcher filters',
              eventLabel: 'studios checked'
            }}
          />
          <CheckBox
            id="rooms-id"
            label={trans('search.filter.rooms')}
            onChange={handleToggleFilterTypeCheckbox('rooms')}
            checked={isPropertyTypeChecked('rooms')}
            soyuzTracking={{
              eventCategory: 'Search',
              eventAction: 'matcher filters',
              eventLabel: 'rooms checked'
            }}
          />
          <CheckBox
            id="residences-id"
            label={trans('search.filter.residences')}
            onChange={handleToggleFilterTypeCheckbox('residences')}
            checked={isPropertyTypeChecked('residences')}
            soyuzTracking={{
              eventCategory: 'Search',
              eventAction: 'matcher filters',
              eventLabel: 'residences checked'
            }}
          />
        </FilterGroup>
      </div>
      <Button
        size="large"
        onClick={handleNext}
        className="matcher__filters-page__cta"
      >
        {trans('search.matcher.filters.cta.next')}
      </Button>
    </>
  );
};

MatcherFilters.propTypes = {
  close: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired
};

export default MatcherFilters;
